import { Component, Inject, OnDestroy, OnInit } from '@angular/core'
import { FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { ApiService, Card, ProgressIndicatorService } from '@mmx/shared'
import moment from 'moment'
import { Subject } from 'rxjs'

@Component({
  selector: 'mmx-patient-credit-card-dialog-component',
  templateUrl: './patient-credit-card-dialog.component.html',
  standalone: false,
})
export class PatientCreditCardDialogComponent implements OnInit, OnDestroy {

  public form: FormGroup
  private onDestroy$ = new Subject<void>()
  currentYear = moment().year()
  currentMonth = moment().month() + 1

  constructor(
    private restService: ApiService,
    public dialogRef: MatDialogRef<PatientCreditCardDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
        card: Card,
        patientId: string
    },
    private progressService: ProgressIndicatorService,
  ) {}

  ngOnDestroy(): void {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  ngOnInit() {
    this.form = new FormGroup({
      expirationMonth: new FormControl('', [Validators.required, Validators.min(1), Validators.max(12)]),
      expirationYear: new FormControl('', [Validators.required, Validators.min(this.currentYear), Validators.max(2099)]),
    }, [this.formValidator().bind(this)])

    this.form.patchValue({
      expirationMonth: this.data.card.expirationMonth,
      expirationYear: this.data.card.expirationYear,
    })
  }

  onSubmit(){
    const card = {
      ...this.data.card,
      expirationMonth: this.form.get('expirationMonth').value,
      expirationYear: this.form.get('expirationYear').value,
    }
    this.restService.updatePatientCard(card, this.data.patientId).subscribe({
      next: (updatedCard) =>
      {
        this.dialogRef.close(updatedCard)
      },
      error: () => {
        this.progressService.snack(
          `An error occurred while updating Card - ${card.lastFourDigits}. Please try again`,
        )
      },
    })
  }

  formValidator(): ValidatorFn {
    return (control: FormGroup): { [key: string]: any } | null => {
      if (control){
        const month = control.controls.expirationMonth.value
        const year = control.controls.expirationYear.value

        if (year === this.currentYear && month <= this.currentMonth) {
          control.controls.expirationMonth.setErrors({ invalidMonthOfYear: true })
          return
        } else if (control.controls.expirationMonth.hasError('invalidMonthOfYear')) {
          control.controls.expirationMonth.setErrors(null)
        }

        return null
      }
    }
  }

  getErrorMessage(controlName: string) {
    const control = this.form.get(controlName)
    if (control) {
      if (control.hasError('required')) {
        return 'Field is required'
      } else if (controlName === 'expirationMonth' && (control.hasError('min') || control.hasError('max'))) {
        return 'Not a valid month.'
      } else if (controlName === 'expirationYear' && (control.hasError('min') || control.hasError('max'))) {
        return 'Not a valid year.'
      } else if (controlName === 'expirationMonth' && control.hasError('invalidMonthOfYear')) {
        return 'Expiration month cannot be in past.'
      }
    }
  }
}
